/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'carbon-react/lib/components/button';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { Logger, QUERY_PARAMS, ROUTES, getForecastByIdOnboardingUrl } from '@futrli/shared-services';
import { UILoader, UISystemIcon, toastError } from '@futrli/ui-components';

import * as styles from '../forecasting-onboarding.scss';
import { OnboardingPageTitle } from '../components/onboarding-page-title';
import { replaceForecast } from '../../../core/api/forecast';
import {
  BUDGET_ONBOARDING_STEP,
  LS_FORECASTING_ONBOARDING_STEP,
  LS_FORECASTING_ONBOARDING_TYPE,
  ONBOARDING_FORECAST_TYPE,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../const';
import { OnboardingPredictionsRouter } from '../components/onboarding-predictions-router';
import { UserGridSettingsContext } from '../../detail/reports/user-grid-settings/user-grid-settings-context';
import { useOnboardingUrlInfo } from '../utils/hooks';
import { usePredictionsInfoContext } from '../../detail/context/predictions-info-context';
import { useIsExcelOrganisation } from '../../../predict-management/hooks/useIsExcelOrganisation';

export enum ForecastType {
  FromScratch = 'from_scratch',
  FromLYA = 'from_lya',
  WithAutoPredictions = 'auto_predictions'
}

export const OnboardingTypeSelect = () => {
  const [selectedForecastType, setSelectedForecastType] = useState<ForecastType | null>(null);
  const { fetchForecast, fetchForecastReport, forecast } = usePredictionsInfoContext();
  const { clearExpandedRows } = useContext(UserGridSettingsContext);

  const intl = useIntl();

  const {
    isOnboardingBudgetRoute,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';
  const [isLoading, setIsLoading] = useState(false);

  const isExcelOrganisation = useIsExcelOrganisation();

  const navigate = useNavigate();

  useEffect(() => {
    clearExpandedRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextDisabled = selectedForecastType === null;

  const handleNextClick = async () => {
    if (nextDisabled || !forecast) {
      return;
    }

    let forecastKind;
    if (isOnboardingBudgetRoute) {
      forecastKind = selectedForecastType === ForecastType.FromLYA ? 'budget-from-actuals' : 'budget-from-scratch';
    } else {
      forecastKind = selectedForecastType === ForecastType.FromLYA ? 'scenario-from-actuals' : 'scenario-from-scratch';
    }

    if (
      forecastKind === 'budget-from-actuals' ||
      forecastKind === 'scenario-from-actuals' ||
      selectedForecastType === ForecastType.WithAutoPredictions
    ) {
      try {
        setIsLoading(true);
        await replaceForecast(workspaceId, organisationId, forecast?.id, {
          ...forecast,
          kind: forecastKind,
          categories_with_ai_enabled:
            selectedForecastType === ForecastType.FromScratch || selectedForecastType === ForecastType.FromLYA
              ? null
              : ['income', 'cost_of_sales', 'expenses', 'other_income', 'other_expenses']
        });
        await fetchForecast?.(undefined, forecast.id);
        await fetchForecastReport?.('all', undefined, forecast.id);
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    }

    if (isOnboardingBudgetRoute) {
      localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, BUDGET_ONBOARDING_STEP.LYA_TOGGLE_OFF);
      localStorage.setItem(LS_FORECASTING_ONBOARDING_TYPE, ONBOARDING_FORECAST_TYPE.BUDGET);
      navigate(
        `${getForecastByIdOnboardingUrl(workspaceId, organisationId, forecast.id, true)}/${
          BUDGET_ONBOARDING_STEP.LYA_TOGGLE_OFF
        }${ROUTES.SALES}?${QUERY_PARAMS.FORECAST_TYPE}=${selectedForecastType}&${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${
          forecast.id
        }`
      );
    } else if (isOnboardingScenarioRoute) {
      localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, SCENARIO_ONBOARDING_STEP.SCRATCH_HISTORICAL);
      localStorage.setItem(LS_FORECASTING_ONBOARDING_TYPE, ONBOARDING_FORECAST_TYPE.SCENARIO);
      navigate(
        `${getForecastByIdOnboardingUrl(workspaceId, organisationId, forecast.id, false)}/${
          SCENARIO_ONBOARDING_STEP.SCRATCH_HISTORICAL
        }${ROUTES.SALES}?${QUERY_PARAMS.FORECAST_TYPE}=${selectedForecastType}&${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${
          forecast.id
        }`
      );
    } else {
      localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_HISTORICAL);
      localStorage.setItem(LS_FORECASTING_ONBOARDING_TYPE, ONBOARDING_FORECAST_TYPE.SCENARIO_WITH_INVOICES);
      navigate(
        `${getForecastByIdOnboardingUrl(workspaceId, organisationId, forecast.id, false, true)}/${
          SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_HISTORICAL
        }${ROUTES.SALES}?${QUERY_PARAMS.FORECAST_TYPE}=${selectedForecastType}&${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${
          forecast.id
        }`
      );
    }
  };

  if (isLoading) {
    return <UILoader isFull />;
  }

  return (
    <div className={styles.contentContainer}>
      <OnboardingPageTitle
        title={
          isOnboardingScenarioWithInvoicesRoute ? (
            <FormattedMessage
              id="onboarding.how_to_create_rest_of_3_way_cash_flow"
              defaultMessage="How do you want to create the rest of your 3 year cash flow?"
            />
          ) : (
            <FormattedMessage id="onboarding.how_to_create_it" defaultMessage="How do you want to create it?" />
          )
        }
        description={
          <FormattedMessage
            id="onboarding.budget_type_explain"
            defaultMessage="You can use these creation flows whenever you want if you need to create more than one budget or forecast on the home page, after you’ve completed this guided flow."
          />
        }
      />
      <div style={{ display: 'flex', marginBottom: 30 }}>
        <div
          className={classNames(styles.onboardingStartButton, styles.onboardingForecastTypeButton, {
            [styles.onboardingStartButtonActive]: selectedForecastType === ForecastType.FromScratch
          })}
          onClick={() => setSelectedForecastType(ForecastType.FromScratch)}
        >
          {selectedForecastType === ForecastType.FromScratch ? (
            <UISystemIcon iconName="double-check-onboarding" width={28} height={28} />
          ) : (
            <UISystemIcon iconName="employee" width={28} height={28} />
          )}
          <FormattedMessage id="onboarding.from_scratch" defaultMessage="From scratch" />
        </div>
        <div
          className={classNames(styles.onboardingStartButton, styles.onboardingForecastTypeButton, {
            [styles.onboardingStartButtonActive]: selectedForecastType === ForecastType.FromLYA
          })}
          style={{ marginLeft: 10 }}
          onClick={() => setSelectedForecastType(ForecastType.FromLYA)}
        >
          {selectedForecastType === ForecastType.FromLYA ? (
            <UISystemIcon iconName="double-check-onboarding" width={28} height={28} />
          ) : (
            <UISystemIcon iconName="bookkeeping" width={28} height={28} />
          )}
          <FormattedMessage id="onboarding.create_from_lya" defaultMessage="Create from last year’s actuals" />
        </div>
        {isExcelOrganisation ? (
          <Tooltip
            position="bottom"
            message={intl.formatMessage({
              id: 'common.forecast_method_not_supported_excel_organisation',
              defaultMessage: 'This forecasting method is not supported with an Excel organisation'
            })}
          >
            <div
              className={classNames(
                styles.onboardingStartButton,
                styles.onboardingForecastTypeButton,
                styles.forecastDataSourceButtonDisabled
              )}
              style={{ marginLeft: 10 }}
            >
              <UISystemIcon iconName="bullseye" width={28} height={28} />
              <FormattedMessage
                id="onboarding.create_with_auto_prediction"
                defaultMessage="Create with auto-predictions"
              />
            </div>
          </Tooltip>
        ) : (
          <div
            className={classNames(styles.onboardingStartButton, styles.onboardingForecastTypeButton, {
              [styles.onboardingStartButtonActive]: selectedForecastType === ForecastType.WithAutoPredictions
            })}
            style={{ marginLeft: 10 }}
            onClick={() => setSelectedForecastType(ForecastType.WithAutoPredictions)}
          >
            {selectedForecastType === ForecastType.WithAutoPredictions ? (
              <UISystemIcon iconName="double-check-onboarding" width={28} height={28} />
            ) : (
              <UISystemIcon iconName="bullseye" width={28} height={28} />
            )}
            <FormattedMessage
              id="onboarding.create_with_auto_prediction"
              defaultMessage="Create with auto-predictions"
            />
          </div>
        )}

        <Button disabled={nextDisabled} ml="30px" onClick={handleNextClick} size="large" buttonType="primary">
          <FormattedMessage id="common.next" defaultMessage="Next" />
        </Button>
      </div>
      <OnboardingPredictionsRouter />
    </div>
  );
};
